import React, { useState, useEffect } from 'react';
import { Card, CardContent, Chip, CircularProgress, Alert, Badge } from '@mui/material';
import { H1, H2, H3, H4 } from '../../components/typography';
import {
  CheckSquare,
  Clock,
  FileText,
  UserCheck,
  AlertCircle,
  Send,
  CreditCard,
  FileSignature,
  ChevronRight,
  Filter,
  Calendar,
  TrendingUp
} from 'lucide-react';
import { useToast } from '../../components/ui/ToastProvider';
import { auth, db } from '../../config/firebase';
import { collection, query, where, getDocs, orderBy, updateDoc, doc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

export default function HyriaTasks() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState('all'); // all, urgent, credit-check, contracts
  const [stats, setStats] = useState({
    total: 0,
    urgent: 0,
    creditChecks: 0,
    contracts: 0,
    completed: 0
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchTasks();
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  async function fetchTasks() {
    setLoading(true);
    try {
      const allTasks = [];

      // 1. Hämta ansökningar som behöver kreditkontroll
      const creditCheckQuery = query(
        collection(db, 'applications'),
        where('status', '==', 'TENANT_INFO_SUBMITTED')
      );
      const creditCheckSnapshot = await getDocs(creditCheckQuery);

      creditCheckSnapshot.forEach((doc) => {
        const data = doc.data();
        const daysSince = Math.floor((new Date() - new Date(data.updatedAt || data.createdAt)) / (1000 * 60 * 60 * 24));

        // Kontrollera kreditkontrollstatus
        if (data.creditCheck?.status === 'consent_received') {
          allTasks.push({
            id: doc.id,
            type: 'perform-credit-check',
            priority: daysSince > 2 ? 'high' : 'normal',
            title: 'Utför kreditkontroll',
            description: `${data.tenantFirstName} ${data.tenantLastName} har gett samtycke`,
            association: data.housingCooperative || data.associationId,
            apartment: data.apartmentNumber,
            createdAt: data.creditCheck.consentDate || data.updatedAt,
            daysSince,
            status: 'pending',
            icon: CreditCard,
            color: 'purple'
          });
        } else if (!data.creditCheck || !data.creditCheck.status) {
          allTasks.push({
            id: doc.id,
            type: 'initiate-credit-check',
            priority: daysSince > 3 ? 'high' : 'normal',
            title: 'Initiera kreditkontroll',
            description: `BRF har godkänt ${data.tenantFirstName} ${data.tenantLastName} - väntar på kreditkontroll`,
            association: data.housingCooperative || data.associationId,
            apartment: data.apartmentNumber,
            createdAt: data.updatedAt || data.createdAt,
            daysSince,
            status: 'pending',
            icon: AlertCircle,
            color: 'orange'
          });
        } else if (data.creditCheck?.status === 'consent_pending') {
          allTasks.push({
            id: doc.id,
            type: 'waiting-consent',
            priority: daysSince > 5 ? 'high' : 'low',
            title: 'Väntar på samtycke',
            description: `Mail skickat till ${data.tenantFirstName} ${data.tenantLastName}`,
            association: data.housingCooperative || data.associationId,
            apartment: data.apartmentNumber,
            createdAt: data.creditCheck.initiatedDate || data.updatedAt,
            daysSince,
            status: 'waiting',
            icon: Send,
            color: 'blue'
          });
        }
      });

      // 2. Hämta ansökningar med begäran om avtal
      const contractQuery = query(
        collection(db, 'applications'),
        where('contractFlow.status', '==', 'contract_requested')
      );
      const contractSnapshot = await getDocs(contractQuery);

      contractSnapshot.forEach((doc) => {
        const data = doc.data();
        const daysSince = Math.floor((new Date() - new Date(data.contractFlow.requestedDate)) / (1000 * 60 * 60 * 24));

        allTasks.push({
          id: doc.id,
          type: 'create-contract',
          priority: daysSince > 1 ? 'high' : 'normal',
          title: 'Skapa hyresavtal',
          description: `${data.tenantFirstName} ${data.tenantLastName} - ${data.address}`,
          association: data.housingCooperative || data.associationId,
          apartment: data.apartmentNumber,
          createdAt: data.contractFlow.requestedDate,
          daysSince,
          status: 'pending',
          icon: FileSignature,
          color: 'green'
        });
      });

      // 3. Hämta ansökningar där kreditkontroll är klar men BRF inte fattat beslut
      const creditCompleteQuery = query(
        collection(db, 'applications'),
        where('status', '==', 'CREDIT_CHECK_COMPLETE')
      );
      const creditCompleteSnapshot = await getDocs(creditCompleteQuery);

      creditCompleteSnapshot.forEach((doc) => {
        const data = doc.data();
        const daysSince = Math.floor((new Date() - new Date(data.creditCheckDate || data.updatedAt)) / (1000 * 60 * 60 * 24));

        if (daysSince > 3) {
          allTasks.push({
            id: doc.id,
            type: 'reminder-brf-decision',
            priority: 'low',
            title: 'Påminn BRF om beslut',
            description: `Kreditkontroll klar sedan ${daysSince} dagar för ${data.tenantFirstName} ${data.tenantLastName}`,
            association: data.housingCooperative || data.associationId,
            apartment: data.apartmentNumber,
            createdAt: data.creditCheckDate || data.updatedAt,
            daysSince,
            status: 'reminder',
            icon: AlertCircle,
            color: 'orange'
          });
        }
      });

      // Sortera uppgifter efter prioritet och datum
      allTasks.sort((a, b) => {
        const priorityOrder = { high: 0, normal: 1, low: 2 };
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setTasks(allTasks);

      // Beräkna statistik
      setStats({
        total: allTasks.length,
        urgent: allTasks.filter(t => t.priority === 'high').length,
        creditChecks: allTasks.filter(t => t.type.includes('credit')).length,
        contracts: allTasks.filter(t => t.type === 'create-contract').length,
        completed: 0 // Kan uppdateras om vi har en completed-status
      });

    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast({ type: 'error', message: 'Kunde inte hämta uppgifter' });
    } finally {
      setLoading(false);
    }
  }

  const handleTaskAction = async (task) => {
    // Här kan vi lägga till specifika åtgärder för olika uppgiftstyper
    switch(task.type) {
      case 'perform-credit-check':
        toast({ type: 'info', message: 'Öppnar kreditkontrollsystem...' });
        // Här kan vi navigera till kreditkontrollsidan eller öppna ett externt system
        break;
      case 'waiting-consent':
        toast({ type: 'info', message: 'Väntar på att hyresgästen ska ge sitt samtycke via e-post.' });
        // Här kan vi eventuellt lägga till funktionalitet för att skicka påminnelse
        break;
      case 'create-contract':
        toast({ type: 'info', message: 'Öppnar avtalshantering...' });
        // Här kan vi navigera till avtalsskapande
        break;
      case 'reminder-brf-decision':
        toast({ type: 'info', message: 'Skickar påminnelse till BRF...' });
        // Här kan vi skicka en påminnelse
        break;
      default:
        toast({ type: 'info', message: 'Öppnar detaljer...' });
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === 'all') return true;
    if (filter === 'urgent') return task.priority === 'high';
    if (filter === 'credit-check') return task.type.includes('credit');
    if (filter === 'contracts') return task.type === 'create-contract';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <H1 className="font-bold text-[#222222]">Uppgifter</H1>
        <p className="text-[#626262] mt-2">Hantera alla väntande uppgifter över hela systemet</p>
      </div>

      {/* Statistik-kort */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="rounded-xl shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <CheckSquare className="w-6 h-6 text-blue-600" />
              </div>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-3xl font-bold text-[#222222]">{stats.total}</div>
            <div className="text-sm text-[#626262] mt-1">Totala uppgifter</div>
          </CardContent>
        </Card>

        <Card className="rounded-xl shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              {stats.urgent > 0 && (
                <Badge badgeContent={stats.urgent} color="error" />
              )}
            </div>
            <div className="text-3xl font-bold text-[#222222]">{stats.urgent}</div>
            <div className="text-sm text-[#626262] mt-1">Brådskande</div>
          </CardContent>
        </Card>

        <Card className="rounded-xl shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <CreditCard className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-[#222222]">{stats.creditChecks}</div>
            <div className="text-sm text-[#626262] mt-1">Kreditkontroller</div>
          </CardContent>
        </Card>

        <Card className="rounded-xl shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <FileSignature className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-[#222222]">{stats.contracts}</div>
            <div className="text-sm text-[#626262] mt-1">Avtal att skapa</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'all'
              ? 'bg-[#e74c3c] text-white'
              : 'bg-white text-[#626262] hover:bg-gray-100'
          }`}
        >
          Alla ({tasks.length})
        </button>
        <button
          onClick={() => setFilter('urgent')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'urgent'
              ? 'bg-[#e74c3c] text-white'
              : 'bg-white text-[#626262] hover:bg-gray-100'
          }`}
        >
          Brådskande ({stats.urgent})
        </button>
        <button
          onClick={() => setFilter('credit-check')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'credit-check'
              ? 'bg-[#e74c3c] text-white'
              : 'bg-white text-[#626262] hover:bg-gray-100'
          }`}
        >
          Kreditkontroller ({stats.creditChecks})
        </button>
        <button
          onClick={() => setFilter('contracts')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'contracts'
              ? 'bg-[#e74c3c] text-white'
              : 'bg-white text-[#626262] hover:bg-gray-100'
          }`}
        >
          Avtal ({stats.contracts})
        </button>
      </div>

      {/* Uppgiftslista */}
      <Card className="rounded-xl shadow-lg">
        <CardContent className="p-0">
          {filteredTasks.length === 0 ? (
            <div className="p-12 text-center">
              <CheckSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <H3 className="text-gray-500">Inga uppgifter att visa</H3>
              <p className="text-gray-400 mt-2">Alla uppgifter är avklarade!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredTasks.map((task) => {
                const IconComponent = task.icon;
                return (
                  <div
                    key={task.id}
                    className="p-6 hover:bg-gray-50 transition-colors cursor-pointer group"
                    onClick={() => handleTaskAction(task)}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-3 bg-${task.color}-100 rounded-lg flex-shrink-0`}
                           style={{
                             backgroundColor:
                               task.color === 'purple' ? '#f3e8ff' :
                               task.color === 'green' ? '#dcfce7' :
                               task.color === 'blue' ? '#dbeafe' :
                               task.color === 'yellow' ? '#fef3c7' :
                               task.color === 'orange' ? '#fed7aa' :
                               '#fee2e2'
                           }}>
                        <IconComponent
                          className="w-6 h-6"
                          style={{
                            color:
                              task.color === 'purple' ? '#9333ea' :
                              task.color === 'green' ? '#22c55e' :
                              task.color === 'blue' ? '#3b82f6' :
                              task.color === 'yellow' ? '#eab308' :
                              task.color === 'orange' ? '#fb923c' :
                              '#ef4444'
                          }}
                        />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-[#222222] text-lg flex items-center gap-2">
                              {task.title}
                              {task.priority === 'high' && (
                                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                                  BRÅDSKANDE
                                </span>
                              )}
                            </h3>
                            <p className="text-[#626262] mt-1">{task.description}</p>

                            <div className="flex items-center gap-4 mt-3 text-sm">
                              <span className="text-[#626262]">
                                🏢 {task.association}
                              </span>
                              <span className="text-[#626262]">
                                🏠 Lgh {task.apartment}
                              </span>
                              <span className="text-[#626262]">
                                ⏱️ {task.daysSince} {task.daysSince === 1 ? 'dag' : 'dagar'} sedan
                              </span>
                            </div>
                          </div>

                          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#e74c3c] transition-colors" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tips */}
      {filteredTasks.length > 0 && (
        <Alert severity="info" className="mt-6">
          <strong>Tips:</strong> Klicka på en uppgift för att öppna relevant sida eller utföra åtgärden.
          Brådskande uppgifter markeras automatiskt baserat på hur länge de har väntat.
        </Alert>
      )}
    </div>
  );
}